import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, LogIn, Eye, AlertTriangle } from 'lucide-react';
import { Button, Badge } from '@staff-app/ui';
import type { CustomerResolveResult, DomainMatchResult } from '@staff-app/shared-types';
import { CustomerSearchBox } from './CustomerSearchBox';
import { CustomerPanel } from './CustomerPanel';
import { useStaffResolveCustomer, useStaffLoginAsCustomer } from '../hooks/useStaffCustomer';

function TeamBadges({ teams }: { teams: CustomerResolveResult['teams'] }) {
  if (!teams?.length) return <span className="text-muted-foreground">{'Default Team'}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {teams.map((t) => (
        <Badge key={t.teamId} variant={t.isCurrent ? 'default' : 'secondary'} className="text-[10px]">
          {t.teamName}{t.isCurrent ? ' ✓' : ''}
        </Badge>
      ))}
    </span>
  );
}

function LoginBtn({ accountId, small = false }: { accountId: string; small?: boolean }) {
  const [login, { loading }] = useStaffLoginAsCustomer();

  const handleLogin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await login({ variables: { accountId } });
      const url = result.data?.staffLoginAsCustomer?.appUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      toast.error('Login failed', { description: (err as Error)?.message ?? 'Unknown error' });
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={handleLogin} disabled={loading} className={small ? 'h-6 gap-1 text-[11px]' : 'h-7 gap-1 text-xs'}>
      <LogIn className="h-3 w-3" />
      {loading ? 'Opening…' : 'Login'}
    </Button>
  );
}

function ResolvedCard({
  resolved,
  onViewEdit,
}: {
  resolved: CustomerResolveResult;
  onViewEdit: () => void;
}) {
  if (!resolved.ownerResolvable) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs">
        <div className="flex items-center gap-1 font-medium text-destructive">
          <AlertTriangle className="h-3.5 w-3.5" />
          {'Account found — owner not resolvable'}
        </div>
        <div className="mt-1 font-mono text-muted-foreground">{resolved.accountId}</div>
        <div className="mt-1 text-muted-foreground">{'Investigate manually.'}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-card p-3 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-semibold text-sm truncate">
            {resolved.companyName ?? resolved.accountId}
          </span>
          <div className="flex flex-wrap gap-1">
            {resolved.plan && (
              <Badge variant="secondary" className="text-[10px]">{resolved.plan}</Badge>
            )}
            {resolved.planFeature && (
              <Badge variant="outline" className="text-[10px]">{resolved.planFeature}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-0.5 text-muted-foreground">
        <div><span className="text-foreground">{'Owner: '}</span>{resolved.ownerEmail}</div>
        {!resolved.inputIsOwner && (
          <div><span className="text-foreground">{'Searched: '}</span>{resolved.inputEmail}</div>
        )}
        <div className="flex items-center gap-1">
          <span className="text-foreground">{'Teams: '}</span>
          <TeamBadges teams={resolved.teams} />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={onViewEdit}>
          <Eye className="h-3 w-3" />
          {'View & Edit'}
        </Button>
        <LoginBtn accountId={resolved.accountId ?? ''} />
      </div>
    </div>
  );
}

function DomainMatchCard({ match, onViewEdit }: { match: DomainMatchResult; onViewEdit: () => void }) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-xs cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={onViewEdit}
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium truncate">{match.companyName ?? match.accountId}</span>
        <span className="text-muted-foreground truncate">{match.ownerEmail ?? '—'}</span>
        <div className="flex flex-wrap gap-1">
          {match.plan && <Badge variant="secondary" className="text-[10px]">{match.plan}</Badge>}
          {match.planFeature && <Badge variant="outline" className="text-[10px]">{match.planFeature}</Badge>}
        </div>
      </div>
      <LoginBtn accountId={match.accountId} small />
    </div>
  );
}

const PAGE_SIZE = 5;

export function UsersSearchColumn() {
  const [query, setQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelResolved, setPanelResolved] = useState<CustomerResolveResult | null>(null);
  const [page, setPage] = useState(0);
  const { data, loading, error } = useStaffResolveCustomer(query);
  const resolved = data?.staffResolveCustomer ?? null;

  // Reset to page 0 whenever query changes
  useEffect(() => { setPage(0); }, [query]);

  const openPanel = (r: CustomerResolveResult) => { setPanelResolved(r); setPanelOpen(true); };

  const openPanelForMatch = (match: DomainMatchResult) => openPanel({
    found: true,
    inputEmail: match.ownerEmail ?? match.accountId,
    ownerResolvable: true,
    accountId: match.accountId,
    companyName: match.companyName,
    plan: match.plan,
    planFeature: match.planFeature,
    ownerEmail: match.ownerEmail,
    ownerName: match.ownerName,
    inputIsOwner: true,
    teams: [],
  });

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="users-search-column"
    >
      <header className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">{'Users'}</h2>
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={'Email, @domain, or cus_…'}
        ariaLabel={'Search users'}
        testId="users-search-box"
      />

      {error ? (
        <p className="text-xs text-destructive" data-testid="users-search-error">
          {'Search failed.'} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Searching…'}</p>
      ) : query.trim().length < 2 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Type to search…'}</p>
      ) : !resolved ? null : resolved.found ? (
        <ResolvedCard
          resolved={resolved}
          onViewEdit={() => openPanel(resolved)}
        />
      ) : resolved.domainMatches != null ? (
        <div className="flex flex-col gap-2">
          {resolved.domainMatches.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              {'No accounts found for this email or domain.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {`Email not found · ${resolved.domainMatches.length} account${resolved.domainMatches.length === 1 ? '' : 's'} matched `}
                <span className="font-mono">{resolved.domainSearched}</span>
              </p>
              {resolved.domainMatches.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((m) => (
                <DomainMatchCard key={m.accountId} match={m} onViewEdit={() => openPanelForMatch(m)} />
              ))}
              {resolved.domainMatches.length > PAGE_SIZE && (
                <div className="flex items-center justify-between pt-1">
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 0}
                  >
                    {'← Prev'}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {`${page + 1} / ${Math.ceil(resolved.domainMatches.length / PAGE_SIZE)}`}
                  </span>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * PAGE_SIZE >= resolved.domainMatches.length}
                  >
                    {'Next →'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      <CustomerPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        resolved={panelResolved}
      />
    </section>
  );
}
